import { useState, useEffect } from 'react';
import { teamsAPI, seriesAPI, divisionsAPI, groundsAPI } from '../../services/api';
import { PageHeader, SectionTitle, Field, DataTable, StatusBadge, Modal, ConfirmDialog, Spinner, EmptyState } from '../../components/ui';
import useAuthStore from '../../store/authStore';
import toast from 'react-hot-toast';

const POOLS = ['A','B','C','D','E','F','G','H'];

const EMPTY_FORM = {
  series_id: '', division_id: '', pool: '',
  name: '', short_name: '',
  home_ground_id: '',
  captain_name: '', captain_contact: '',
  vc_name: '', vc_contact: '',
  cricclubs_team_id: '',
  is_blacklisted: false, blacklist_reason: '',
  admin_comments: ''
};

const EMPTY_UMPIRE = { name: '', contact: '', is_certified: false };

export default function TeamsPage() {
  const { canEdit, isAdmin, isTeamUser } = useAuthStore();
  const [seriesList, setSeriesList] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [grounds, setGrounds] = useState([]);
  const [teams, setTeams] = useState([]);
  const [filterSeries, setFilterSeries] = useState('');
  const [filterDivision, setFilterDivision] = useState('');
  const [filterPool, setFilterPool] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [umpires, setUmpires] = useState([{ ...EMPTY_UMPIRE }]);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [activeTab, setActiveTab] = useState('details');

  useEffect(() => { loadSeries(); loadGrounds(); }, []);
  useEffect(() => { if (filterSeries) { loadDivisions(filterSeries); loadTeams(); } }, [filterSeries]);
  useEffect(() => { loadTeams(); }, [filterDivision, filterPool]);
  useEffect(() => { if (form.series_id) loadDivisionsForForm(form.series_id); }, [form.series_id]);

  const loadSeries = async () => {
    try {
      const { data } = await seriesAPI.list();
      setSeriesList(data);
      if (data.length > 0) setFilterSeries(data[0].id);
    } catch { toast.error('Failed to load series'); }
  };

  const loadDivisions = async (seriesId) => {
    try {
      const { data } = await divisionsAPI.list(seriesId);
      setDivisions(data);
    } catch {}
  };

  const loadDivisionsForForm = async (seriesId) => {
    try {
      const { data } = await divisionsAPI.list(seriesId);
      setDivisions(data);
    } catch {}
  };

  const loadGrounds = async () => {
    try {
      const { data } = await groundsAPI.list();
      setGrounds(data);
    } catch {}
  };

  const loadTeams = async () => {
    if (!filterSeries) return;
    setLoading(true);
    try {
      const params = { series_id: filterSeries };
      if (filterDivision) params.division_id = filterDivision;
      if (filterPool) params.pool = filterPool;
      const { data } = await teamsAPI.list(params);
      setTeams(data);
    } catch { toast.error('Failed to load teams'); }
    finally { setLoading(false); }
  };

  const setField = (key, value) => setForm(p => ({ ...p, [key]: value }));

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, series_id: filterSeries, division_id: filterDivision });
    setUmpires([{ ...EMPTY_UMPIRE }]);
    setEditingId(null);
    setActiveTab('details');
    setShowForm(true);
  };

  const openEdit = (t) => {
    setForm({
      series_id: t.series_id, division_id: t.division_id, pool: t.pool || '',
      name: t.name, short_name: t.short_name || '',
      home_ground_id: t.home_ground_id || '',
      captain_name: t.captain_name || '', captain_contact: t.captain_contact || '',
      vc_name: t.vc_name || '', vc_contact: t.vc_contact || '',
      cricclubs_team_id: t.cricclubs_team_id || '',
      is_blacklisted: t.is_blacklisted || false, blacklist_reason: t.blacklist_reason || '',
      admin_comments: t.admin_comments || ''
    });
    setUmpires(t.umpires?.length > 0 ? t.umpires : [{ ...EMPTY_UMPIRE }]);
    setEditingId(t.id);
    setActiveTab('details');
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.series_id || !form.division_id || !form.name || !form.short_name)
      return toast.error('Series, division, name and short name are required');
    setSaving(true);
    try {
      const payload = { ...form, home_ground_id: form.home_ground_id || null, cricclubs_team_id: form.cricclubs_team_id || null };
      let teamId = editingId;
      if (editingId) { await teamsAPI.update(editingId, payload); toast.success('Team updated'); }
      else {
        const { data } = await teamsAPI.create(payload);
        teamId = data.id;
        toast.success('Team created');
      }
      // Save umpires
      const validUmpires = umpires.filter(u => u.name.trim());
      for (const u of validUmpires) {
        if (!u.id) await teamsAPI.addUmpire(teamId, u);
      }
      setShowForm(false);
      loadTeams();
    } catch (err) { toast.error(err.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try {
      await teamsAPI.delete(showDeleteConfirm);
      toast.success('Team deleted');
      setShowDeleteConfirm(null);
      loadTeams();
    } catch (err) { toast.error(err.response?.data?.error || 'Delete failed'); }
  };

  const addUmpire = () => setUmpires(p => [...p, { ...EMPTY_UMPIRE }]);
  const updateUmpire = (i, key, val) => setUmpires(p => p.map((u, idx) => idx === i ? { ...u, [key]: val } : u));
  const removeUmpire = (i) => setUmpires(p => p.filter((_, idx) => idx !== i));

  const filteredTeams = teams.filter(t =>
    !search || t.name.toLowerCase().includes(search.toLowerCase()) || t.short_name?.toLowerCase().includes(search.toLowerCase())
  );

  const availablePools = divisions.find(d => d.id === filterDivision)?.pools || [];

  return (
    <div>
      <PageHeader icon="◉" title="Teams"
        subtitle="Registration, captains, umpires and CricClubs sync."
        actions={canEdit() && <button className="btn btn-primary" onClick={openCreate}>+ Add Team</button>}
      />

      {/* Filters */}
      <div className="card" style={{ padding: '12px 16px', marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <select className="input select" style={{ width: 180 }} value={filterSeries} onChange={e => { setFilterSeries(e.target.value); setFilterDivision(''); setFilterPool(''); }}>
          {seriesList.map(s => <option key={s.id} value={s.id}>{s.short_name} {s.season_year}</option>)}
        </select>
        <select className="input select" style={{ width: 160 }} value={filterDivision} onChange={e => { setFilterDivision(e.target.value); setFilterPool(''); }}>
          <option value="">All Divisions</option>
          {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        {availablePools.length > 0 && (
          <select className="input select" style={{ width: 120 }} value={filterPool} onChange={e => setFilterPool(e.target.value)}>
            <option value="">All Pools</option>
            {availablePools.map(p => <option key={p} value={p}>Pool {p}</option>)}
          </select>
        )}
        <input className="input" style={{ width: 200 }} placeholder="Search teams..." value={search} onChange={e => setSearch(e.target.value)} />
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--mid)' }}>{filteredTeams.length} team{filteredTeams.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Teams table */}
      <div className="card" style={{ padding: 20 }}>
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner size={28} /></div>
        ) : filteredTeams.length === 0 ? (
          <EmptyState icon="◉" title="No teams found"
            description="No teams match the current filters."
            action={canEdit() && <button className="btn btn-primary" onClick={openCreate}>+ Add Team</button>}
          />
        ) : (
          <DataTable
            headers={['#', 'Team', 'Division', 'Pool', 'Captain', 'Home Ground', 'CricClubs', 'Status', ...(canEdit() ? ['Actions'] : [])]}
            rows={filteredTeams.map((t, i) => [
              <span className="mono" style={{ color: 'var(--mid)', fontSize: 11 }}>{i + 1}</span>,
              <div>
                <div style={{ fontWeight: 700, fontSize: 13 }}>{t.name}</div>
                <div className="mono" style={{ fontSize: 10, color: 'var(--accent)', marginTop: 1 }}>{t.short_name}</div>
              </div>,
              <span style={{ fontSize: 12 }}>{t.division_name || '—'}</span>,
              t.pool ? <span className="tag tag-blue">Pool {t.pool}</span> : <span style={{ color: 'var(--dim)' }}>—</span>,
              <div>
                <div style={{ fontSize: 12 }}>{t.captain_name || '—'}</div>
                {t.captain_contact && <div style={{ fontSize: 10, color: 'var(--mid)' }}>{t.captain_contact}</div>}
              </div>,
              <span style={{ fontSize: 12 }}>{t.home_ground_name || '—'}</span>,
              t.cricclubs_team_id
                ? <span className="tag tag-green">Synced</span>
                : <span className="tag tag-mid">Not linked</span>,
              t.is_blacklisted
                ? <span className="tag tag-red">Blacklisted</span>
                : <span className="tag tag-green">Active</span>,
              ...(canEdit() ? [
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => openEdit(t)}>Edit</button>
                  {isAdmin() && <button className="btn btn-danger btn-sm" onClick={() => setShowDeleteConfirm(t.id)}>Delete</button>}
                </div>
              ] : [])
            ])}
          />
        )}
      </div>

      {/* Add/Edit Modal */}
      {showForm && (
        <Modal title={editingId ? 'Edit Team' : 'Add Team'} onClose={() => setShowForm(false)} width={620}>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
            {['details', 'captain', 'umpires', 'admin'].map(tab => (
              <button key={tab} onClick={() => setActiveTab(tab)}
                style={{ padding: '8px 16px', fontSize: 12, fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', borderBottom: activeTab === tab ? '2px solid var(--accent)' : '2px solid transparent', color: activeTab === tab ? 'var(--accent)' : 'var(--mid)', textTransform: 'capitalize', marginBottom: -1 }}>
                {tab === 'captain' ? 'Captain & VC' : tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {activeTab === 'details' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Series" required>
                <select className="input select" value={form.series_id} onChange={e => { setField('series_id', e.target.value); setField('division_id', ''); }}>
                  <option value="">Select series...</option>
                  {seriesList.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </Field>
              <Field label="Division" required>
                <select className="input select" value={form.division_id} onChange={e => { setField('division_id', e.target.value); setField('pool', ''); }}>
                  <option value="">Select division...</option>
                  {divisions.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </Field>
              <div style={{ gridColumn: '1 / -1' }}>
                <Field label="Pool" note="Select the pool this team belongs to">
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {(divisions.find(d => d.id === form.division_id)?.pools || POOLS).map(p => (
                      <div key={p} onClick={() => setField('pool', form.pool === p ? '' : p)}
                        style={{ width: 34, height: 34, borderRadius: 6, cursor: 'pointer', border: `1.5px solid ${form.pool === p ? 'var(--accent)' : 'var(--border)'}`, background: form.pool === p ? 'rgba(0,200,150,.12)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: form.pool === p ? 'var(--accent)' : 'var(--dim)', transition: 'all .15s' }}>
                        {p}
                      </div>
                    ))}
                  </div>
                </Field>
              </div>
              <Field label="Team Name" required>
                <input className="input" placeholder="e.g. Grand Panthers CC" value={form.name} onChange={e => setField('name', e.target.value)} />
              </Field>
              <Field label="Short Name" required>
                <input className="input" placeholder="e.g. GPAN" value={form.short_name} onChange={e => setField('short_name', e.target.value.toUpperCase())} />
              </Field>
              <div style={{ gridColumn: '1 / -1' }}>
                <Field label="Home Ground">
                  <select className="input select" value={form.home_ground_id} onChange={e => setField('home_ground_id', e.target.value)}>
                    <option value="">No home ground</option>
                    {grounds.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                  </select>
                </Field>
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <Field label="CricClubs Team ID" note="Optional — from cricclubs.com">
                  <input className="input mono" placeholder="e.g. 4521" value={form.cricclubs_team_id} onChange={e => setField('cricclubs_team_id', e.target.value)} />
                </Field>
              </div>
            </div>
          )}

          {activeTab === 'captain' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Captain Name">
                <input className="input" placeholder="Full name" value={form.captain_name} onChange={e => setField('captain_name', e.target.value)} />
              </Field>
              <Field label="Captain Contact">
                <input className="input" placeholder="+1 xxx xxx xxxx" value={form.captain_contact} onChange={e => setField('captain_contact', e.target.value)} />
              </Field>
              <Field label="Vice Captain">
                <input className="input" placeholder="Full name" value={form.vc_name} onChange={e => setField('vc_name', e.target.value)} />
              </Field>
              <Field label="VC Contact">
                <input className="input" placeholder="+1 xxx xxx xxxx" value={form.vc_contact} onChange={e => setField('vc_contact', e.target.value)} />
              </Field>
            </div>
          )}

          {activeTab === 'umpires' && (
            <div>
              <SectionTitle color="var(--purple)">Umpires</SectionTitle>
              {umpires.map((u, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto auto', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                  <input className="input" placeholder={`Umpire ${i + 1} name`} value={u.name} onChange={e => updateUmpire(i, 'name', e.target.value)} />
                  <input className="input" placeholder="Contact (optional)" value={u.contact} onChange={e => updateUmpire(i, 'contact', e.target.value)} />
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    <input type="checkbox" checked={u.is_certified} onChange={e => updateUmpire(i, 'is_certified', e.target.checked)} />
                    Certified
                  </label>
                  {umpires.length > 1 && (
                    <button className="btn btn-danger btn-sm" onClick={() => removeUmpire(i)} style={{ padding: '4px 8px' }}>✕</button>
                  )}
                </div>
              ))}
              <button className="btn btn-ghost btn-sm" onClick={addUmpire} style={{ marginTop: 4 }}>+ Add Umpire</button>
            </div>
          )}

          {activeTab === 'admin' && isAdmin() && (
            <div>
              <SectionTitle color="var(--red)">Admin Controls</SectionTitle>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 7, border: `1.5px solid ${form.is_blacklisted ? 'var(--red)' : 'var(--border)'}`, background: form.is_blacklisted ? 'rgba(239,68,68,.07)' : 'transparent', cursor: 'pointer', marginBottom: 12 }}>
                <input type="checkbox" checked={form.is_blacklisted} onChange={e => setField('is_blacklisted', e.target.checked)} />
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: form.is_blacklisted ? 'var(--red)' : 'var(--text)' }}>Blacklist Team</div>
                  <div style={{ fontSize: 11, color: 'var(--mid)' }}>Blacklisted teams cannot be scheduled for matches</div>
                </div>
              </label>
              {form.is_blacklisted && (
                <Field label="Blacklist Reason">
                  <input className="input" placeholder="Reason for blacklisting..." value={form.blacklist_reason} onChange={e => setField('blacklist_reason', e.target.value)} />
                </Field>
              )}
              <Field label="Admin Comments / Notes">
                <textarea className="input" rows={4} placeholder="Internal notes about this team..." value={form.admin_comments} onChange={e => setField('admin_comments', e.target.value)} style={{ resize: 'vertical' }} />
              </Field>
            </div>
          )}

          <hr className="divider" />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? <><Spinner size={14} /> Saving...</> : editingId ? '✓ Update Team' : '+ Add Team'}
            </button>
          </div>
        </Modal>
      )}

      {showDeleteConfirm && (
        <ConfirmDialog danger
          message="Delete this team? All fixtures involving this team will also be removed. This cannot be undone."
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(null)}
        />
      )}
    </div>
  );
}